import { useEffect, useState } from "react";
import { Save, X } from "lucide-react";
import { Apirequests } from "../../utils/ApiRequests";
import { ErrorTost, SuccessTost } from "../../utils/ToastHelper";
import BtnSpinner from "../../components/BtnSpinner";

function AddOrEditUpcomingClass({ close, setRefresh, currentClass }) {
  const token = localStorage.getItem("token");

  const [data, setData] = useState({
    course_id: "",
    course_name: "",
    title: "",
    course_date: "",
    image: "",
    price: "",
    link: "",
    redirectUri: "",
    listId: "",
    status: "Active",
    serial_number: "",
  });

  const [isEdit, setIsEdit] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState({ state: false, name: "" });

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files?.[0];

      if (file) {
        setImageFile(file);
        setPreviewImage(URL.createObjectURL(file));
      }

      return;
    }

    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addImage = async (file) => {
    if (!file) return { success: false, message: "No file selected" };

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await fetch(
        process.env.REACT_APP_SERVER_URI + "image/upload-image",
        {
          method: "POST",
          body: formData,
        }
      );

      return await response.json();
    } catch (error) {
      return { success: false, message: "Error uploading image" };
    }
  };

  const handleReset = () => {
    setData({
      course_id: "",
      course_name: "",
      title: "",
      course_date: "",
      image: "",
      price: "",
      link: "",
      redirectUri: "",
      listId: "",
      status: "Active",
      serial_number: "",
    });

    setPreviewImage("");
    setImageFile(null);
    setIsEdit(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading({
      state: true,
      name: currentClass ? "editclass" : "addclass",
    });

    let formData = {
      ...data,
      price: Number(data.price || 0),
      serial_number: Number(data.serial_number || 0),
    };

    if (imageFile) {
      const imageRes = await addImage(imageFile);

      if (imageRes?.success) {
        formData.image = imageRes?.data;
      } else {
        ErrorTost(imageRes?.message || "Failed to upload image");
        setLoading({ state: false, name: "" });
        return;
      }
    } else if (currentClass?.image) {
      formData.image = currentClass.image;
    }

    if (currentClass) {
      await handleEditClass(currentClass?._id, formData);
    } else {
      await handleAddClass(formData);
    }
  };

  const handleAddClass = async (formData) => {
    try {
      const res = await Apirequests(
        "masterclass/upcoming-master-classes",
        "POST",
        formData,
        {
          Authorization: `Bearer ${token}`,
        }
      );

      if (res?.success) {
        SuccessTost(res?.message);
        close();
        setRefresh((prev) => !prev);
      } else {
        ErrorTost(res?.message || "Failed to create upcoming master class");
      }
    } catch (error) {
      console.log(error, "create upcoming master class error");
      ErrorTost("Something went wrong, please try again later.");
    }

    setLoading({ state: false, name: "" });
  };

  const handleEditClass = async (id, formData) => {
    try {
      const res = await Apirequests(
        `masterclass/upcoming-master-classes/${id}`,
        "PUT",
        formData,
        {
          Authorization: `Bearer ${token}`,
        }
      );

      if (res?.success) {
        SuccessTost(res?.message);
        close();
        setRefresh((prev) => !prev);
      } else {
        ErrorTost(res?.message || "Failed to update upcoming master class");
      }
    } catch (error) {
      console.log(error, "edit upcoming master class error");
      ErrorTost("Something went wrong, please try again later.");
    }

    setLoading({ state: false, name: "" });
  };

  useEffect(() => {
    if (currentClass) {
      setData({
        course_id: currentClass?.course_id || "",
        course_name: currentClass?.course_name,
        title: currentClass?.title || "",
        course_date: currentClass?.course_date || "",
        image: currentClass?.image || "",
        price: currentClass?.price || "",
        link: currentClass?.link || "",
        redirectUri: currentClass?.redirectUri,
        listId: currentClass?.listId,
        status: currentClass?.status || "Active",
        serial_number: currentClass?.serial_number || "",
      });

      if (currentClass?.image) {
        setPreviewImage(
          `${process.env.REACT_APP_IMAGE_URI}${currentClass.image}`
        );
      }

      setIsEdit(true);
    }

    return () => {
      handleReset();
    };
  }, [currentClass]);

  return (
    <div className="py-2 px-2 sm:px-2 lg:px-2">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? "Edit Upcoming Master Class" : "Add Upcoming Master Class"}
        </h1>

        {!isEdit && (
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700"
          >
            Reset Form
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Image Preview
          </label>

          <div className="relative flex items-center justify-center h-56 w-full bg-gray-100 rounded-lg overflow-hidden">
            {previewImage ? (
              <img
                src={previewImage}
                alt="Upcoming master class preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-gray-400">No image preview available</div>
            )}
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Course Id *
          </label>
          <input
            type="text"
            name="course_id"
            value={data.course_id}
            onChange={handleInputChange}
            placeholder="example: relationship-masterclass"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Course Name
          </label>
          <input
            type="text"
            name="course_name"
            value={data.course_name}
            onChange={handleInputChange}
            placeholder="Enter Course Name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title *
          </label>
          <input
            type="text"
            name="title"
            value={data.title}
            onChange={handleInputChange}
            placeholder="Enter title"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Course Date *
          </label>
          <input
            type="text"
            name="course_date"
            value={data.course_date}
            onChange={handleInputChange}
            placeholder="example: 13th Feb"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Image
          </label>
          <input
            type="file"
            name="image"
            onChange={handleInputChange}
            accept="image/*"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price *
          </label>
          <input
            type="number"
            name="price"
            value={data.price}
            onChange={handleInputChange}
            placeholder="Enter price"
            required
            min="0"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Link
          </label>
          <input
            type="text"
            name="link"
            value={data.link}
            onChange={handleInputChange}
            placeholder="/relationship-masterclass"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Redirect Url/ Thank You Page *
          </label>
          <input
            type="text"
            name="redirectUri"
            value={data.redirectUri}
            onChange={handleInputChange}
            placeholder="Enter Thank you page url"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Serial Number
          </label>
          <input
            type="number"
            name="serial_number"
            value={data.serial_number}
            onChange={handleInputChange}
            placeholder="Enter serial number"
            min="0"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            List Id(Brevo list Id) *
          </label>
          <input
            type="text"
            name="listId"
            value={data.listId}
            onChange={handleInputChange}
            placeholder="Enter title"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status *
          </label>
          <select
            name="status"
            value={data.status}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading.state}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-70"
          >
            {loading.state ? (
              <BtnSpinner />
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span className="text-white">
                  {isEdit ? "Update Class" : "Create Class"}
                </span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={close}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddOrEditUpcomingClass;